import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Chart from 'react-apexcharts';
import { useFieldAuth } from '../contexts/FieldContext';
import { useRefereeAuth } from '../contexts/RefereeContext';
import { fieldAnalyticsService } from '../services/fieldAnalyticsService';
import { FieldAnalytics, DateRange } from '../types/analytics';
import {
  AnalyticsContainer,
  AnalyticsHeader,
  PageTitle,
  AnalyticsGrid,
  ChartCard,
  ChartTitle,
  MetricsGrid,
  MetricCard,
  MetricValue,
  MetricLabel,
  FullWidthChart,
  DatePickerWrapper,
  NoAccessContainer,
  TopPlayersTable,
  BackButton,
  TitleWithTooltip,
} from '../styles/analyticsStyles';
import {
  areaChartOptions,
  barChartOptions,
  horizontalBarChartOptions,
  donutChartOptions,
  radialBarChartOptions,
  lineChartOptions,
} from '../components/analytics/charts/chartTheme';
import TopPlayersSection from '../components/analytics/TopPlayersSection';
import Tooltip from '../components/analytics/Tooltip';
import { FeatureGate } from '../components/subscription';

// Tab types
type AnalyticsTab = 'overview' | 'games' | 'maps' | 'referees' | 'players' | 'devices' | 'operations' | 'templates';

// Styled components for tabs
const TabsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  border: 1px solid rgba(0, 212, 255, 0.15);
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 24px;
  background: ${({ $active }) => $active ? 'rgba(0, 212, 255, 0.2)' : 'transparent'};
  border: 1px solid ${({ $active }) => $active ? '#00d4ff' : 'rgba(0, 212, 255, 0.2)'};
  border-radius: 8px;
  color: ${({ $active }) => $active ? '#00d4ff' : '#8bb8d8'};
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: rgba(0, 212, 255, 0.15);
    border-color: rgba(0, 212, 255, 0.5);
    color: #00d4ff;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 20px;
`;

const Spinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid rgba(0, 212, 255, 0.1);
  border-top: 4px solid #00d4ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  color: #00d4ff;
  font-family: 'Courier New', monospace;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #ff4444;
  font-family: 'Courier New', monospace;
  text-align: center;
  gap: 16px;
`;

const RetryButton = styled.button`
  padding: 10px 24px;
  background: rgba(0, 212, 255, 0.15);
  border: 1px solid #00d4ff;
  border-radius: 8px;
  color: #00d4ff;
  font-family: 'Courier New', monospace;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.25);
  }
`;

const SectionTitle = styled.h2`
  color: #00d4ff;
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.5rem;
  margin: 24px 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  padding-bottom: 8px;
`;

const getDefaultDateRange = (): DateRange => {
  const now = new Date();
  const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    from: firstDayOfMonth,
    to: lastDayOfMonth,
  };
};

const formatDate = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

// Game type filter options
type GameTypeFilter = 'all' | 'airsoft' | 'paintball' | 'speedball';

const GameTypeSelect = styled.select`
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  font-weight: bold;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00d4ff;
  }

  option {
    background: #1a1a2e;
    color: #ffffff;
    font-weight: bold;
  }
`;

const FilterLabel = styled.span`
  color: #8bb8d8;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  margin-right: 8px;
`;

const FieldAnalyticsPage: React.FC = () => {
  const { fieldId } = useParams<{ fieldId: string }>();
  const navigate = useNavigate();
  const { field, isFieldAuthenticated } = useFieldAuth();
  const { referee, isRefereeAuthenticated } = useRefereeAuth();

  const [analytics, setAnalytics] = useState<FieldAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('overview');
  const [loadedTabs, setLoadedTabs] = useState<Set<AnalyticsTab>>(new Set());
  const [gameTypeFilter, setGameTypeFilter] = useState<GameTypeFilter>('all');

  // Check access
  const hasAccess = useMemo(() => {
    if (isFieldAuthenticated && field?.id === fieldId) return true;
    if (
      isRefereeAuthenticated &&
      referee?.isMainReferee &&
      Object.keys(referee?.fieldAssignments || {}).includes(fieldId!)
    )
      return true;
    return false;
  }, [fieldId, field, referee, isFieldAuthenticated, isRefereeAuthenticated]);

  // Helper to check if analytics section has data
  const hasData = (obj: any): boolean => {
    if (!obj) return false;
    if (Array.isArray(obj)) return obj.length > 0;
    if (typeof obj === 'object') {
      // Check if object has any non-zero/non-empty values
      return Object.values(obj).some(val => {
        if (typeof val === 'number') return val !== 0;
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === 'object' && val !== null) return hasData(val);
        return Boolean(val);
      });
    }
    return Boolean(obj);
  };

  // Fetch specific tabs data
  const fetchTabData = useCallback(async (tabs: AnalyticsTab[]) => {
    if (!fieldId || !hasAccess) return;

    try {
      setTabLoading(true);
      const data = await fieldAnalyticsService.getFieldAnalytics(
        fieldId,
        dateRange.from,
        dateRange.to,
        gameTypeFilter,
        tabs
      );

      // Merge new data with existing analytics - only overwrite if new data has values
      setAnalytics(prev => {
        if (!prev) return data;
        return {
          supportedGameTypes: data.supportedGameTypes?.length ? data.supportedGameTypes : prev.supportedGameTypes,
          overview: hasData(data.overview) ? data.overview : prev.overview,
          gamesAnalytics: hasData(data.gamesAnalytics) ? data.gamesAnalytics : prev.gamesAnalytics,
          mapAnalytics: hasData(data.mapAnalytics) ? data.mapAnalytics : prev.mapAnalytics,
          refereeAnalytics: hasData(data.refereeAnalytics) ? data.refereeAnalytics : prev.refereeAnalytics,
          playerAnalytics: hasData(data.playerAnalytics) ? data.playerAnalytics : prev.playerAnalytics,
          deviceAnalytics: hasData(data.deviceAnalytics) ? data.deviceAnalytics : prev.deviceAnalytics,
          operationsAnalytics: hasData(data.operationsAnalytics) ? data.operationsAnalytics : prev.operationsAnalytics,
          templatesAnalytics: hasData(data.templatesAnalytics) ? data.templatesAnalytics : prev.templatesAnalytics,
        };
      });

      // Mark tabs as loaded
      setLoadedTabs(prev => {
        const newSet = new Set(prev);
        tabs.forEach(tab => newSet.add(tab));
        return newSet;
      });
    } catch (err: any) {
      console.error('Error fetching tab data:', err);
      setError(err.response?.data?.message || 'Failed to load tab data');
    } finally {
      setTabLoading(false);
    }
  }, [fieldId, dateRange, hasAccess, gameTypeFilter]);

  // Initial load - fetch overview tab (includes games and players for Quick Summary charts)
  const fetchInitialData = useCallback(async () => {
    if (!fieldId || !hasAccess) {
      setInitialLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Load overview, games and players together for Quick Summary section
      const data = await fieldAnalyticsService.getFieldAnalytics(
        fieldId,
        dateRange.from,
        dateRange.to,
        gameTypeFilter,
        ['overview', 'games', 'players']
      );
      setAnalytics(data);
      setLoadedTabs(new Set<AnalyticsTab>(['overview', 'games', 'players']));
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [fieldId, dateRange, hasAccess, gameTypeFilter]);

  // Initial load
  useEffect(() => {
    if (hasAccess && !analytics) {
      fetchInitialData();
    } else if (!hasAccess) {
      setInitialLoading(false);
    }
  }, [hasAccess]);

  // Reset and reload when date range or game type filter changes
  useEffect(() => {
    if (analytics) {
      // Clear loaded tabs and reload
      setLoadedTabs(new Set<AnalyticsTab>());
      setAnalytics(null);

      // Reload initial data, then reload current tab if it's not in initial set
      const initialTabs = ['overview', 'games', 'players'];
      if (!initialTabs.includes(activeTab)) {
        // Need to reload both initial and current tab
        fieldAnalyticsService.getFieldAnalytics(
          fieldId!,
          dateRange.from,
          dateRange.to,
          gameTypeFilter,
          [...initialTabs, activeTab]
        ).then(data => {
          setAnalytics(data);
          setLoadedTabs(new Set<AnalyticsTab>(['overview', 'games', 'players', activeTab]));
        }).catch(err => {
          console.error('Error fetching analytics:', err);
          setError(err.response?.data?.message || 'Failed to load analytics');
        });
      } else {
        fetchInitialData();
      }
    }
  }, [dateRange, gameTypeFilter]);

  // Handle tab change - load tab data if not already loaded
  const handleTabChange = useCallback((tab: AnalyticsTab) => {
    setActiveTab(tab);

    // If tab data is not loaded yet, fetch it
    if (!loadedTabs.has(tab)) {
      fetchTabData([tab]);
    }
  }, [loadedTabs, fetchTabData]);

  const handleDateChange = (type: 'from' | 'to', value: string) => {
    const newDate = new Date(value);
    setDateRange((prev) => ({
      ...prev,
      [type]: newDate,
    }));
  };

  // Access denied - field/referee permission check
  if (!hasAccess && !initialLoading) {
    return (
      <NoAccessContainer>
        <h2>Access Denied</h2>
        <p>You don't have permission to view analytics for this field.</p>
        <p>Only field owners and main referees can access this page.</p>
        <BackButton onClick={() => navigate(-1)}>Go Back</BackButton>
      </NoAccessContainer>
    );
  }

  // Initial loading state
  if (initialLoading || (loading && !analytics)) {
    return (
      <AnalyticsContainer>
        <LoadingContainer>
          <Spinner />
          <LoadingText>Loading Analytics...</LoadingText>
        </LoadingContainer>
      </AnalyticsContainer>
    );
  }

  // Error state
  if (error && !analytics) {
    return (
      <AnalyticsContainer>
        <ErrorContainer>
          <div>{error}</div>
          <RetryButton onClick={fetchInitialData}>Retry</RetryButton>
          <BackButton onClick={() => navigate(-1)}>Go Back</BackButton>
        </ErrorContainer>
      </AnalyticsContainer>
    );
  }

  if (!analytics) {
    return (
      <AnalyticsContainer>
        <ErrorContainer>No data available</ErrorContainer>
      </AnalyticsContainer>
    );
  }

  // Helper to check if a game type is supported
  const supportedTypes = analytics.supportedGameTypes || [];
  const hasAirsoft = supportedTypes.includes('airsoft');
  const hasPaintball = supportedTypes.includes('paintball');
  const hasSpeedball = supportedTypes.includes('speedball');

  // Prepare chart data - dynamically based on supported game types
  // Games tab data (only if loaded)
  const gamesOverTimeSeries: { name: string; data: number[] }[] = [];
  const gamesOverTime = analytics.gamesAnalytics?.gamesOverTime || [];
  if (hasAirsoft && gamesOverTime.length > 0) {
    gamesOverTimeSeries.push({ name: 'Airsoft', data: gamesOverTime.map((d) => d.airsoft) });
  }
  if (hasPaintball && gamesOverTime.length > 0) {
    gamesOverTimeSeries.push({ name: 'Paintball', data: gamesOverTime.map((d) => d.paintball) });
  }
  if (hasSpeedball && gamesOverTime.length > 0) {
    gamesOverTimeSeries.push({ name: 'Speedball', data: gamesOverTime.map((d) => d.speedball) });
  }

  const gamesOverTimeData = {
    series: gamesOverTimeSeries,
    categories: gamesOverTime.map((d) => d.date),
  };

  // Games by type - dynamically
  const gamesByTypeSeries: number[] = [];
  const gamesByTypeLabels: string[] = [];
  const gamesByType = analytics.gamesAnalytics?.gamesByType;
  if (hasAirsoft && gamesByType) {
    gamesByTypeSeries.push(gamesByType.airsoft);
    gamesByTypeLabels.push('Airsoft');
  }
  if (hasPaintball && gamesByType) {
    gamesByTypeSeries.push(gamesByType.paintball);
    gamesByTypeLabels.push('Paintball');
  }
  if (hasSpeedball && gamesByType) {
    gamesByTypeSeries.push(gamesByType.speedball);
    gamesByTypeLabels.push('Speedball');
  }

  const gamesByTypeData = {
    series: gamesByTypeSeries,
    labels: gamesByTypeLabels,
  };

  // Avg duration by type - dynamically
  const avgDurationValues: number[] = [];
  const avgDurationCategories: string[] = [];
  const avgDurationByType = analytics.gamesAnalytics?.avgDurationByType;
  if (hasAirsoft && avgDurationByType) {
    avgDurationValues.push(avgDurationByType.airsoft);
    avgDurationCategories.push('Airsoft');
  }
  if (hasPaintball && avgDurationByType) {
    avgDurationValues.push(avgDurationByType.paintball);
    avgDurationCategories.push('Paintball');
  }
  if (hasSpeedball && avgDurationByType) {
    avgDurationValues.push(avgDurationByType.speedball);
    avgDurationCategories.push('Speedball');
  }

  const avgDurationData = {
    series: [{ name: 'Minutes', data: avgDurationValues }],
    categories: avgDurationCategories,
  };

  const winRateByFaction = analytics.gamesAnalytics?.winRateByFaction;
  const winRateData = {
    series: winRateByFaction ? [winRateByFaction.human, winRateByFaction.alien] : [0, 0],
    labels: ['Human', 'Alien'],
  };

  // Maps tab data
  const missionsByMap = analytics.mapAnalytics?.missionsByMap || [];
  const mapUsageData = {
    series: [{ name: 'Missions', data: missionsByMap.map((m) => m.totalMissions) }],
    categories: missionsByMap.map((m) => m.mapName),
  };

  // Referees tab data
  const hoursWorked = analytics.refereeAnalytics?.hoursWorked || [];
  const gamesPerReferee = analytics.refereeAnalytics?.gamesPerReferee || [];
  const refereeHoursData = {
    series: [{ name: 'Hours', data: hoursWorked.map((r) => r.hours || 0) }],
    categories: hoursWorked.map((r) => r.refereeName),
  };

  const refereeGamesData = {
    series: [{ name: 'Games', data: gamesPerReferee.map((r) => r.games || 0) }],
    categories: gamesPerReferee.map((r) => r.refereeName),
  };

  // Players tab data
  const participationTrends = analytics.playerAnalytics?.participationTrends || [];
  const newVsReturning = analytics.playerAnalytics?.newVsReturning;
  const participationTrendsData = {
    series: [{ name: 'Players', data: participationTrends.map((p) => p.uniquePlayers) }],
    categories: participationTrends.map((p) => p.date),
  };

  const newVsReturningData = {
    series: newVsReturning ? [newVsReturning.newPlayers, newVsReturning.returningPlayers] : [0, 0],
    labels: ['New Players', 'Returning Players'],
  };

  // Devices tab data
  const topDevices = analytics.deviceAnalytics?.topDevices || [];
  const deviceInteractionsData = {
    series: [{ name: 'Interactions', data: topDevices.map((d) => d.interactions) }],
    categories: topDevices.map((d) => d.deviceName),
  };

  // Operations tab data
  const popularOperations = analytics.operationsAnalytics?.popularOperations || [];
  const operationsChartData = {
    series: [{ name: 'Times Played', data: popularOperations.map((o) => o.timesPlayed) }],
    categories: popularOperations.map((o) => o.operationName),
  };

  // Templates tab data - now grouped by operations
  const operationsWithTemplates = analytics.templatesAnalytics?.operationsWithTemplates || [];
  const templatesChartData = {
    series: [{ name: 'Times Played', data: operationsWithTemplates.map((o) => o.totalTimesPlayed) }],
    categories: operationsWithTemplates.map((o) => o.operationName),
  };

  // Check if sections have data (used for showing/hiding tabs)
  // For lazy loading, we show tabs and let them load their own data
  const hasMapsData = loadedTabs.has('maps') ? missionsByMap.length > 0 : true;
  const hasRefereesData = loadedTabs.has('referees') ? (hoursWorked.length > 0 || gamesPerReferee.length > 0) : true;
  const hasDevicesData = loadedTabs.has('devices') ? topDevices.length > 0 : true;
  const hasOperationsData = loadedTabs.has('operations') ? popularOperations.length > 0 : true;
  const hasTemplatesData = loadedTabs.has('templates') ? operationsWithTemplates.length > 0 : true;

  // Tab content loading wrapper
  const TabContent: React.FC<{ tab: AnalyticsTab; children: React.ReactNode }> = ({ tab, children }) => {
    // Show loading only if this specific tab is not loaded yet
    // If tab is already loaded (in cache), show content even if other tabs are loading
    if (!loadedTabs.has(tab)) {
      return (
        <LoadingContainer>
          <Spinner />
          <LoadingText>Loading {tab}...</LoadingText>
        </LoadingContainer>
      );
    }
    return <>{children}</>;
  };

  return (
    <FeatureGate feature="analytics" featureName="Analytics Dashboard">
      <AnalyticsContainer>
        <AnalyticsHeader>
          <PageTitle>Field Analytics</PageTitle>
        <DatePickerWrapper>
          <FilterLabel>Type:</FilterLabel>
          <GameTypeSelect
            value={gameTypeFilter}
            onChange={(e) => setGameTypeFilter(e.target.value as GameTypeFilter)}
          >
            <option value="all">All Types</option>
            {hasAirsoft && <option value="airsoft">Airsoft</option>}
            {hasPaintball && <option value="paintball">Paintball</option>}
            {hasSpeedball && <option value="speedball">Speedball</option>}
          </GameTypeSelect>
          <FilterLabel style={{ marginLeft: '16px' }}>From:</FilterLabel>
          <input
            type="date"
            value={formatDate(dateRange.from)}
            onChange={(e) => handleDateChange('from', e.target.value)}
          />
          <span>to</span>
          <input
            type="date"
            value={formatDate(dateRange.to)}
            onChange={(e) => handleDateChange('to', e.target.value)}
          />
        </DatePickerWrapper>
      </AnalyticsHeader>

      {/* Loading overlay when refreshing data */}
      {loading && analytics && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(0, 0, 0, 0.8)',
          padding: '30px',
          borderRadius: '12px',
          border: '1px solid rgba(0, 212, 255, 0.3)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <Spinner />
          <LoadingText>Updating...</LoadingText>
        </div>
      )}

      {/* Tabs */}
      <TabsContainer>
        <Tab $active={activeTab === 'overview'} onClick={() => handleTabChange('overview')}>
          Overview
        </Tab>
        <Tab $active={activeTab === 'games'} onClick={() => handleTabChange('games')}>
          Missions
        </Tab>
        {hasMapsData && (
          <Tab $active={activeTab === 'maps'} onClick={() => handleTabChange('maps')}>
            Maps
          </Tab>
        )}
        {hasRefereesData && (
          <Tab $active={activeTab === 'referees'} onClick={() => handleTabChange('referees')}>
            Referees
          </Tab>
        )}
        <Tab $active={activeTab === 'players'} onClick={() => handleTabChange('players')}>
          Players
        </Tab>
        {hasDevicesData && (
          <Tab $active={activeTab === 'devices'} onClick={() => handleTabChange('devices')}>
            Devices
          </Tab>
        )}
        {hasOperationsData && (
          <Tab $active={activeTab === 'operations'} onClick={() => handleTabChange('operations')}>
            Operations
          </Tab>
        )}
        {hasTemplatesData && (
          <Tab $active={activeTab === 'templates'} onClick={() => handleTabChange('templates')}>
            Templates
          </Tab>
        )}
      </TabsContainer>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <TabContent tab="overview">
          <MetricsGrid>
            <MetricCard>
              <MetricValue>{analytics.overview?.totalGames ?? 0}</MetricValue>
              <TitleWithTooltip>
                <MetricLabel>Total Missions</MetricLabel>
                <Tooltip content="Total number of completed missions at this field" />
              </TitleWithTooltip>
            </MetricCard>
            <MetricCard>
              <MetricValue>{analytics.overview?.totalPlayers ?? 0}</MetricValue>
              <TitleWithTooltip>
                <MetricLabel>Total Players</MetricLabel>
                <Tooltip content="Total unique players who participated in missions" />
              </TitleWithTooltip>
            </MetricCard>
            <MetricCard>
              <MetricValue>{analytics.overview?.totalRefereeHours ?? 0}h</MetricValue>
              <TitleWithTooltip>
                <MetricLabel>Referee Hours</MetricLabel>
                <Tooltip content="Total hours from referee schedules during the selected period" />
              </TitleWithTooltip>
            </MetricCard>
            <MetricCard>
              <MetricValue>{analytics.overview?.avgGameDuration ?? 0}m</MetricValue>
              <TitleWithTooltip>
                <MetricLabel>Avg Duration</MetricLabel>
                <Tooltip content="Average mission duration in minutes" />
              </TitleWithTooltip>
            </MetricCard>
            <MetricCard>
              <MetricValue>{analytics.overview?.gamesThisPeriod ?? 0}</MetricValue>
              <TitleWithTooltip>
                <MetricLabel>Missions This Period</MetricLabel>
                <Tooltip content="Number of missions completed in the selected date range" />
              </TitleWithTooltip>
            </MetricCard>
            <MetricCard>
              <MetricValue>{analytics.overview?.playersThisPeriod ?? 0}</MetricValue>
              <TitleWithTooltip>
                <MetricLabel>Players This Period</MetricLabel>
                <Tooltip content="Unique players who participated in missions during the selected period" />
              </TitleWithTooltip>
            </MetricCard>
          </MetricsGrid>

          {/* Quick summary charts */}
          <SectionTitle>Quick Summary</SectionTitle>
          <AnalyticsGrid>
            <ChartCard>
              <TitleWithTooltip>
                <ChartTitle>Missions by Type</ChartTitle>
                <Tooltip content="Distribution of missions by game type (Airsoft, Paintball, Speedball)" />
              </TitleWithTooltip>
              <Chart
                options={{ ...donutChartOptions, labels: gamesByTypeData.labels }}
                series={gamesByTypeData.series}
                type="donut"
                height={280}
              />
            </ChartCard>
            <ChartCard>
              <TitleWithTooltip>
                <ChartTitle>Win Rate by Faction</ChartTitle>
                <Tooltip content="Percentage of games won by Human vs Alien faction" />
              </TitleWithTooltip>
              <Chart
                options={{ ...radialBarChartOptions, labels: winRateData.labels }}
                series={winRateData.series}
                type="radialBar"
                height={280}
              />
            </ChartCard>
            <ChartCard>
              <TitleWithTooltip>
                <ChartTitle>New vs Returning Players</ChartTitle>
                <Tooltip content="New players first played during this period. Returning players played before this period." />
              </TitleWithTooltip>
              <Chart
                options={{ ...donutChartOptions, labels: newVsReturningData.labels }}
                series={newVsReturningData.series}
                type="donut"
                height={280}
              />
            </ChartCard>
          </AnalyticsGrid>
        </TabContent>
      )}

      {/* Missions Tab */}
      {activeTab === 'games' && (
        <TabContent tab="games">
          <FullWidthChart>
            <TitleWithTooltip>
              <ChartTitle>Missions Over Time</ChartTitle>
              <Tooltip content="Number of missions played each day, broken down by game type" />
            </TitleWithTooltip>
            <Chart
              options={{
                ...areaChartOptions,
                xaxis: { ...areaChartOptions.xaxis, categories: gamesOverTimeData.categories },
              }}
              series={gamesOverTimeData.series}
              type="area"
              height={350}
            />
          </FullWidthChart>

          <AnalyticsGrid>
            <ChartCard>
              <TitleWithTooltip>
                <ChartTitle>Missions by Type</ChartTitle>
                <Tooltip content="Total missions for each game type in the selected period" />
              </TitleWithTooltip>
              <Chart
                options={{ ...donutChartOptions, labels: gamesByTypeData.labels }}
                series={gamesByTypeData.series}
                type="donut"
                height={300}
              />
            </ChartCard>
            <ChartCard>
              <TitleWithTooltip>
                <ChartTitle>Average Duration (minutes)</ChartTitle>
                <Tooltip content="Average mission duration for each game type" />
              </TitleWithTooltip>
              <Chart
                options={{
                  ...barChartOptions,
                  xaxis: { ...barChartOptions.xaxis, categories: avgDurationData.categories },
                }}
                series={avgDurationData.series}
                type="bar"
                height={300}
              />
            </ChartCard>
            <ChartCard>
              <TitleWithTooltip>
                <ChartTitle>Win Rate by Faction</ChartTitle>
                <Tooltip content="Percentage of victories for Human and Alien factions" />
              </TitleWithTooltip>
              <Chart
                options={{ ...radialBarChartOptions, labels: winRateData.labels }}
                series={winRateData.series}
                type="radialBar"
                height={300}
              />
            </ChartCard>
          </AnalyticsGrid>
        </TabContent>
      )}

      {/* Maps Tab */}
      {activeTab === 'maps' && hasMapsData && (
        <TabContent tab="maps">
          <FullWidthChart>
            <TitleWithTooltip>
              <ChartTitle>Missions by Map</ChartTitle>
              <Tooltip content="Number of missions played on each map during the selected period" />
            </TitleWithTooltip>
            <Chart
              options={{
                ...horizontalBarChartOptions,
                xaxis: { ...horizontalBarChartOptions.xaxis, categories: mapUsageData.categories },
              }}
              series={mapUsageData.series}
              type="bar"
              height={Math.max(350, mapUsageData.categories.length * 50)}
            />
          </FullWidthChart>
        </TabContent>
      )}

      {/* Referees Tab */}
      {activeTab === 'referees' && hasRefereesData && (
        <TabContent tab="referees">
          <AnalyticsGrid>
            {analytics.refereeAnalytics.hoursWorked.length > 0 && (
              <ChartCard>
                <TitleWithTooltip>
                  <ChartTitle>Hours Worked</ChartTitle>
                  <Tooltip content="Total hours from referee schedules during the selected period" />
                </TitleWithTooltip>
                <Chart
                  options={{
                    ...horizontalBarChartOptions,
                    xaxis: { ...horizontalBarChartOptions.xaxis, categories: refereeHoursData.categories },
                  }}
                  series={refereeHoursData.series}
                  type="bar"
                  height={Math.max(300, refereeHoursData.categories.length * 45)}
                />
              </ChartCard>
            )}
            {analytics.refereeAnalytics.gamesPerReferee.length > 0 && (
              <ChartCard>
                <TitleWithTooltip>
                  <ChartTitle>Games per Referee</ChartTitle>
                  <Tooltip content="Number of missions managed by each referee" />
                </TitleWithTooltip>
                <Chart
                  options={{
                    ...horizontalBarChartOptions,
                    xaxis: { ...horizontalBarChartOptions.xaxis, categories: refereeGamesData.categories },
                  }}
                  series={refereeGamesData.series}
                  type="bar"
                  height={Math.max(300, refereeGamesData.categories.length * 45)}
                />
              </ChartCard>
            )}
          </AnalyticsGrid>
        </TabContent>
      )}

      {/* Players Tab */}
      {activeTab === 'players' && (
        <TabContent tab="players">
          <FullWidthChart>
            <TitleWithTooltip>
              <ChartTitle>Player Participation Trends</ChartTitle>
              <Tooltip content="Unique players participating each day during the period" />
            </TitleWithTooltip>
            <Chart
              options={{
                ...lineChartOptions,
                xaxis: { ...lineChartOptions.xaxis, categories: participationTrendsData.categories },
              }}
              series={participationTrendsData.series}
              type="line"
              height={350}
            />
          </FullWidthChart>

          <AnalyticsGrid>
            <ChartCard>
              <TitleWithTooltip>
                <ChartTitle>New vs Returning Players</ChartTitle>
                <Tooltip content="New players first played during this period. Returning players played before this period." />
              </TitleWithTooltip>
              <Chart
                options={{ ...donutChartOptions, labels: newVsReturningData.labels }}
                series={newVsReturningData.series}
                type="donut"
                height={300}
              />
            </ChartCard>
          </AnalyticsGrid>

          {/* Top 100 Players Section with custom filters */}
          <TopPlayersSection fieldId={fieldId!} supportedGameTypes={supportedTypes} />
        </TabContent>
      )}

      {/* Devices Tab */}
      {activeTab === 'devices' && hasDevicesData && (
        <TabContent tab="devices">
          <FullWidthChart>
            <TitleWithTooltip>
              <ChartTitle>Top Devices by Interactions</ChartTitle>
              <Tooltip content="Devices with the most player interactions during missions" />
            </TitleWithTooltip>
            <Chart
              options={{
                ...horizontalBarChartOptions,
                xaxis: { ...horizontalBarChartOptions.xaxis, categories: deviceInteractionsData.categories },
              }}
              series={deviceInteractionsData.series}
              type="bar"
              height={Math.max(350, deviceInteractionsData.categories.length * 50)}
            />
          </FullWidthChart>

          {analytics.deviceAnalytics.topPlayersByInteractions.length > 0 && (
            <ChartCard style={{ marginTop: '24px' }}>
              <TitleWithTooltip>
                <ChartTitle>Top Players by Device Interactions</ChartTitle>
                <Tooltip content="Players who had the most interactions with devices during missions" />
              </TitleWithTooltip>
              <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                <TopPlayersTable>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Player</th>
                      <th>Interactions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.deviceAnalytics.topPlayersByInteractions.map((player, index) => (
                      <tr key={player.userId}>
                        <td>{index + 1}</td>
                        <td>{player.username}</td>
                        <td>{player.interactions}</td>
                      </tr>
                    ))}
                  </tbody>
                </TopPlayersTable>
              </div>
            </ChartCard>
          )}
        </TabContent>
      )}

      {/* Operations Tab */}
      {activeTab === 'operations' && hasOperationsData && (
        <TabContent tab="operations">
          {/* Operations Summary */}
          <MetricsGrid>
            <MetricCard>
              <MetricValue>{analytics.operationsAnalytics?.totalOperationsPlayed || 0}</MetricValue>
              <TitleWithTooltip>
                <MetricLabel>Total Games Played</MetricLabel>
                <Tooltip content="Total number of games played using all operations" />
              </TitleWithTooltip>
            </MetricCard>
            <MetricCard>
              <MetricValue>{analytics.operationsAnalytics?.uniqueOperations || 0}</MetricValue>
              <TitleWithTooltip>
                <MetricLabel>Unique Operations</MetricLabel>
                <Tooltip content="Number of different operations used at this field" />
              </TitleWithTooltip>
            </MetricCard>
          </MetricsGrid>

          <FullWidthChart>
            <TitleWithTooltip>
              <ChartTitle>Most Played Operations</ChartTitle>
              <Tooltip content="Operations ranked by how many times they were played" />
            </TitleWithTooltip>
            <Chart
              options={{
                ...horizontalBarChartOptions,
                xaxis: { ...horizontalBarChartOptions.xaxis, categories: operationsChartData.categories },
              }}
              series={operationsChartData.series}
              type="bar"
              height={Math.max(350, operationsChartData.categories.length * 50)}
            />
          </FullWidthChart>

          {/* Operations Details Table */}
          <ChartCard style={{ marginTop: '24px' }}>
            <TitleWithTooltip>
              <ChartTitle>Operations Statistics</ChartTitle>
              <Tooltip content="Detailed stats for each operation: play count, avg duration, and faction win rates" />
            </TitleWithTooltip>
            <div style={{ overflowX: 'auto' }}>
              <TopPlayersTable>
                <thead>
                  <tr>
                    <th>Operation</th>
                    <th>Times Played</th>
                    <th>Avg Duration</th>
                    <th>Human Win %</th>
                    <th>Alien Win %</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics.operationsAnalytics?.popularOperations || []).map((op) => (
                    <tr key={op.operationId}>
                      <td>{op.operationName}</td>
                      <td>{op.timesPlayed}</td>
                      <td>{op.avgDuration}m</td>
                      <td style={{ color: op.winRateHuman > op.winRateAlien ? '#4caf50' : '#8bb8d8' }}>
                        {op.winRateHuman}%
                      </td>
                      <td style={{ color: op.winRateAlien > op.winRateHuman ? '#f44336' : '#8bb8d8' }}>
                        {op.winRateAlien}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TopPlayersTable>
            </div>
          </ChartCard>
        </TabContent>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && hasTemplatesData && (
        <TabContent tab="templates">
          {/* Templates Summary */}
          <MetricsGrid>
            <MetricCard>
              <MetricValue>{analytics.templatesAnalytics?.totalTemplatesPlayed || 0}</MetricValue>
              <TitleWithTooltip>
                <MetricLabel>Total Games Played</MetricLabel>
                <Tooltip content="Total number of games played using all templates" />
              </TitleWithTooltip>
            </MetricCard>
            <MetricCard>
              <MetricValue>{analytics.templatesAnalytics?.uniqueTemplates || 0}</MetricValue>
              <TitleWithTooltip>
                <MetricLabel>Unique Templates</MetricLabel>
                <Tooltip content="Number of different templates used at this field" />
              </TitleWithTooltip>
            </MetricCard>
            <MetricCard>
              <MetricValue>{analytics.templatesAnalytics?.uniqueOperations || 0}</MetricValue>
              <TitleWithTooltip>
                <MetricLabel>Operations</MetricLabel>
                <Tooltip content="Number of different operations with templates" />
              </TitleWithTooltip>
            </MetricCard>
          </MetricsGrid>

          {/* Templates grouped by Operations */}
          {operationsWithTemplates.map((operation) => (
            <ChartCard key={operation.operationId} style={{ marginTop: '24px' }}>
              <TitleWithTooltip>
                <ChartTitle>
                  {operation.operationName}
                  <span style={{
                    fontSize: '0.8rem',
                    color: '#8bb8d8',
                    marginLeft: '12px',
                    fontWeight: 'normal'
                  }}>
                    ({operation.totalTimesPlayed} times played)
                  </span>
                </ChartTitle>
                <Tooltip content="Templates used for this operation with their stats" />
              </TitleWithTooltip>
              <div style={{ overflowX: 'auto' }}>
                <TopPlayersTable>
                  <thead>
                    <tr>
                      <th>Template</th>
                      <th>Times Played</th>
                      <th>Avg Duration</th>
                      <th>Human Win %</th>
                      <th>Alien Win %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operation.templates.map((t) => (
                      <tr key={t.templateId}>
                        <td>{t.templateName}</td>
                        <td>{t.timesPlayed}</td>
                        <td>{t.avgDuration}m</td>
                        <td style={{ color: t.winRateHuman > t.winRateAlien ? '#4caf50' : '#8bb8d8' }}>
                          {t.winRateHuman}%
                        </td>
                        <td style={{ color: t.winRateAlien > t.winRateHuman ? '#f44336' : '#8bb8d8' }}>
                          {t.winRateAlien}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </TopPlayersTable>
              </div>
            </ChartCard>
          ))}
        </TabContent>
      )}

      <div style={{ marginTop: '40px', textAlign: 'center' }}>
        <BackButton onClick={() => navigate(`/fields/${fieldId}`)}>Back to Field</BackButton>
      </div>
      </AnalyticsContainer>
    </FeatureGate>
  );
};

export default FieldAnalyticsPage;
